/**
 * Оболочка рабочих экранов: вкладки и бейджи.
 *
 * Вкладки появляются по мере готовности экранов — приложение не показывает
 * разделов, за которыми ничего нет. Бейдж — единственный способ, которым
 * приложение просит внимания изнутри (17.12).
 */

import { useEffect } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'

import { useFamily, useRole } from '@/app/session'
import { CoachTour } from '@/features/onboarding/CoachTour'
import { WhatsNew } from '@/features/whatsnew/WhatsNew'
import { useUnreadReleases } from '@/features/whatsnew/releases'
import { track } from '@/shared/analytics'
import { useDay } from '@/shared/api/day'
import { TabBar, type TabItem } from '@/shared/ui/TabBar'

export function ParentShell() {
  const family = useFamily()
  const day = useDay(family?.id ?? 0, undefined, false)
  const pending = day.data?.expenses.filter((item) => item.status === 'pending').length ?? 0

  const tabs: TabItem[] = [
    { to: '/today', label: 'Сегодня', icon: 'nav-day' },
    { to: '/schedule', label: 'Расписание', icon: 'nav-schedule' },
    { to: '/pay', label: 'Оплата', icon: 'nav-money', coach: 'parent_pay' },
    { to: '/family', label: 'Семья', icon: 'nav-family', badge: pending, coach: 'parent_family' },
  ]

  return <Shell tabs={tabs} fallback="/today" />
}

export function NannyShell() {
  // Значки те же, что у родителя: «Мой день» и «Заработок» — это день и
  // деньги, только с другой стороны. Разные картинки намекали бы на разные
  // разделы, а раздел один.
  const tabs: TabItem[] = [
    { to: '/day', label: 'Мой день', icon: 'nav-day' },
    { to: '/schedule', label: 'Расписание', icon: 'nav-schedule' },
    { to: '/earnings', label: 'Заработок', icon: 'nav-money', coach: 'nanny_earnings' },
    { to: '/profile', label: 'Профиль', icon: 'nav-profile' },
  ]

  return <Shell tabs={tabs} fallback="/day" />
}

// Экраны без вкладки: открываются из разделов, но живут внутри оболочки.
const EXTRA_ROUTES = ['/trust']

function Shell({ tabs, fallback }: { tabs: TabItem[]; fallback: string }) {
  const location = useLocation()
  const role = useRole()
  const unread = useUnreadReleases(role)

  // Единственное клиентское событие в приложении: какой экран открыли.
  // Имя экрана — код маршрута, никаких данных семьи.
  useEffect(() => {
    track('screen_view', { screen: location.pathname.replace('/', '') || 'root' })
  }, [location.pathname])

  const known =
    tabs.some((tab) => location.pathname.startsWith(tab.to)) ||
    EXTRA_ROUTES.some((route) => location.pathname.startsWith(route))

  if (!known) return <Navigate to={fallback} replace />

  return (
    <>
      <Outlet />
      <TabBar items={tabs} />
      {/* Новости выпуска и подсказки первого входа взаимно исключают друг
          друга: два слоя поверх экрана сразу читаются как поломка. Новости
          идут первыми — они короче и закрываются одним тапом. */}
      {unread.length > 0 ? <WhatsNew /> : role && <CoachTour role={role} />}
    </>
  )
}
